import * as CL from '@design-system/component-library';
import * as React from 'react';
import { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables.js';
import { ShoppingBasketCoCoScope } from '../../common/enums.js';
import {
  chosenLanguageMsg,
  closeMsg,
  englishMsg,
  forTheEmployeeMsg,
  logInMsg,
  logOutMsg,
  myInformationMsg,
  suomiMsg,
  t,
} from '../../common/i18n/index.js';
import { getPublishedCatalogs } from '../../common/utils/catalogUtils.js';
import { userHasApproverRole } from '../../common/utils/employeeUtils.js';
import { isStrictFeatureEnabled } from '../../common/utils/featureFlagUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { IdentityProvider } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useFeatureFlags } from '../../public/site/ConfigProvider.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { isElisaIdV3Enabled } from '../../public/site/authUtils.js';
import { Header as PublicHeader } from '../Header/Header.js';
import { siteSelect } from '../Header/dynamic/headerFunctions.js';
import { HeaderShoppingBasketCoCo } from '../HeaderShoppingCart/HeaderShoppingBasketCoCo.js';
import { HeaderShoppingCart } from '../HeaderShoppingCart/HeaderShoppingCart.js';
import { SmallHeader } from '../SmallHeader/SmallHeader.js';
import { eoeHeaderAccordions } from './dynamic/eoeHeaderAccordions.js';
import { eoeNavigationLinksWithCurrentProperty } from './dynamic/eoeNavigationLinks.js';
import { eoeUserMenuLinks } from './dynamic/eoeUserMenuLinks.js';
import type { State } from '../../selfservice/common/store.js';

import './EoeHeader.scss';

export const EoeHeader = () => {
  const cocoEnabled = isStrictFeatureEnabled(useFeatureFlags().coco);
  const { logout, ssoSessionValid, authenticatedUser, getUser, prepareElisaIdLogin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { siteBaseUrl } = useContext(SiteContext);
  const { siteLanguage, saveUserPreferredLanguage } = React.useContext(SiteContext);
  const menuId = 'employee-oma-elisa-navigation';
  const hamburgerRef = useRef<HTMLAnchorElement | HTMLButtonElement>(null);

  const { isSmallHeaderShown, virtualCatalogItems } = useSelector(
    (state: State) => ({
      isSmallHeaderShown: !state.layout?.hideHeaderFooter,
      virtualCatalogItems: state.selfservice?.virtualCatalogs?.items,
    }),
    deepEqual
  );

  const isLoggedIn = !!authenticatedUser?.userName;
  const { firstName = '', lastName = '', email = '', companyName = '' } = authenticatedUser || {};
  const hasApproverRole = userHasApproverRole(authenticatedUser);
  const isElisaIdUser = isElisaIdV3Enabled() && authenticatedUser?.identityProvider === IdentityProvider.ELISA_ID;
  const hasPublishedCatalogs = virtualCatalogItems
    ? (getPublishedCatalogs(virtualCatalogItems) || []).length > 0
    : false;
  const navigationItemList = eoeNavigationLinksWithCurrentProperty(
    hasApproverRole,
    hasPublishedCatalogs,
    pathname,
    siteBaseUrl
  );
  const personalLinks = eoeUserMenuLinks(
    navigate,
    hasApproverRole,
    false,
    isElisaIdUser ? prepareElisaIdLogin : undefined
  );
  const accordions = eoeHeaderAccordions();

  const languageSelect = {
    languages: [
      { name: suomiMsg, abbr: 'fi' },
      { name: englishMsg, abbr: 'en' },
    ],
    selectedLanguage: siteLanguage.substring(0, 2),
    onLanguageChange: ({ lang }: { lang: string }) => {
      saveUserPreferredLanguage(lang);
    },
  };

  useEffect(() => {
    if (ssoSessionValid) {
      getUser();
    }
  }, [getUser, ssoSessionValid]);

  const employeeLogout = () => {
    logout(paths.EMPLOYEE_LOGOUT);
  };

  return (
    <>
      {isSmallHeaderShown && decodeURI(pathname).startsWith(`${paths.EMPLOYEE_EMAIL_VERIFICATION}/`) ? (
        <SmallHeader />
      ) : isLoggedIn ? (
        <CL.Header
          desktopBreakpointOffset={35}
          className={`of-employee-header ${isElisaIdUser ? 'of-employee-header--elisa-id' : ''}`}
          isLoggedIn={isLoggedIn}
          languageSelect={languageSelect}
          siteSelect={siteSelect(siteBaseUrl)}
        >
          <CL.HeaderNavigationSection>
            <CL.HeaderLogoAndService
              logo={{
                label: 'Elisa',
                onClick: e => {
                  e.preventDefault();
                  navigate(paths.EMPLOYEE_HOME);
                },
                url: '/',
              }}
              service={
                companyName
                  ? {
                      identifiers: ['OmaElisa', t.ACGC(forTheEmployeeMsg)],
                      label: '',
                      onClick: e => {
                        e.preventDefault();
                        navigate(paths.EMPLOYEE_HOME);
                      },
                      url: paths.EMPLOYEE_HOME,
                    }
                  : undefined
              }
            />
          </CL.HeaderNavigationSection>
          <CL.HeaderNavigationSection>
            <CL.HeaderNavigation
              id={menuId}
              languageSelect={languageSelect}
              navigationItemList={navigationItemList}
              onButtonClick={() => {}}
              onLinkClick={(e, { url }) => {
                const isAbsolute = new RegExp('^([a-z]+://|//)', 'i');
                if (url && !isAbsolute.test(url)) {
                  e.preventDefault();
                  navigate(url);
                }
              }}
              requireLogin={false}
              returnFocusToRef={hamburgerRef}
              siteSelect={siteSelect(siteBaseUrl)}
            />
          </CL.HeaderNavigationSection>
          <CL.HeaderNavigationSection>
            {cocoEnabled ? (
              <HeaderShoppingBasketCoCo
                toShoppingCart={paths.EMPLOYEE_SHOPPING_CART}
                scope={ShoppingBasketCoCoScope.EMPLOYEE}
              />
            ) : (
              <HeaderShoppingCart
                toShoppingCart={paths.EMPLOYEE_SHOPPING_CART}
                toCheckout={paths.EMPLOYEE_DEVICE_CHECKOUT}
              />
            )}
            <CL.HeaderUserMenu
              i18nButtonLabel={t.MIFJ(myInformationMsg)}
              i18nLanguageSwitchSelectedLanguageLabel={t.FT4V(chosenLanguageMsg)}
              i18nLogin={t.Z16I(logInMsg)}
              logout={{
                onClick: employeeLogout,
                title: t.LQ3X(logOutMsg),
              }}
              notifications={personalLinks}
              personalLinks={[]}
              user={
                authenticatedUser
                  ? {
                      email,
                      fullName: `${firstName} ${lastName}`,
                      name: `${firstName} ${lastName}`,
                    }
                  : undefined
              }
              userPronounText={firstName}
              userMenuLinkAccordions={accordions}
            />
            <CL.HeaderHamburger
              i18nCloseAlt={t.WOYD(closeMsg)}
              i18nOpenAlt="menu"
              componentRef={hamburgerRef}
              toggledModalChild={menuId}
            />
          </CL.HeaderNavigationSection>
        </CL.Header>
      ) : (
        <PublicHeader />
      )}
    </>
  );
};
